import { omit } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { getApiStatus } from '../../../../../core/utils';
import { saveEmployee, SAVE_EMPLOYEE } from '../../../../employees/actions';
import Employee from '../../../../employees/models';
import useForm, { Form, FormValues, userFields } from '../../../hooks/useForm';
import { selectOperatorGroup, selectOperatorGroupId } from '../../../../../store/authReducer';

type EmployeeFields = Exclude<FormValues, 'userGroupId' | 'coreSites'>;

const useEditForm = (employee: Employee): Form & { isNew: boolean; employee?: Employee } => {
  const dispatch = useDispatch();
  const operatorGroupId = useSelector(selectOperatorGroupId);
  const operatorGroup = useSelector(selectOperatorGroup);

  const isLoading = useSelector((state) => getApiStatus(state, SAVE_EMPLOYEE));

  const getDefaultValues = (): FormValues => {
    let loginCredentialsNeeded = false;

    if (employee.coreUser) {
      loginCredentialsNeeded = !employee.coreUser?.archivedAt;
    }

    return {
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      phoneNumber: employee.phoneNumber,
      position: employee.position,
      loginCredentialsNeeded: loginCredentialsNeeded,
      isCashier: employee.isCashier,
      isManager: employee.isManager,
      userGroupId: employee.coreUser?.userGroupId || null,
      coreSites: employee.coreUser?.sites || [],
      emailOptOut: Boolean(employee.coreUser?.emailOptOut),
      cashierSites: employee.cashierSites
        ? employee.cashierSites.map((site) => ({ id: site.id }))
        : [],
    };
  };

  const formatBody = (values: FormValues): any => {
    const formattedValues: any = { ...values };

    if (formattedValues.cashierSites) {
      formattedValues.cashierSites = formattedValues.cashierSites.map((site: any) => site.id);
    }

    if (!values.loginCredentialsNeeded) {
      return omit(formattedValues, userFields);
    }

    return formattedValues;
  };

  const handleSubmit = (values: FormValues) => {
    const formattedBody = formatBody(values);
    const patchBody = {
      data: {
        body: {
          id: employee.id,
          ...formattedBody,
          operatorGroup: {
            id: operatorGroupId ? operatorGroupId : 3,
            name: operatorGroup?.name,
            details: { email: operatorGroup?.email },
          },
        },
      },
      closeModal: false,
    };
    dispatch(saveEmployee(patchBody));
  };

  const formProps = useForm({
    isLoading,
    onSubmit: handleSubmit,
    initialValues: getDefaultValues(),
  });

  return {
    ...formProps,
    employee,
    isNew: false,
  };
};

export default useEditForm;
